import type { ReactNode } from "react";
import { Sentry } from "@/lib/sentry";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw } from "lucide-react";

interface Props {
  children: ReactNode;
}

function FallbackUi({ resetError }: { resetError: () => void }) {
  return (
    <div
      className="min-h-screen w-full flex items-center justify-center px-6 py-12 bg-background"
      data-testid="sentry-error-fallback"
    >
      <div className="max-w-md w-full rounded-md border bg-card p-6 text-center space-y-4">
        <div className="mx-auto h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center">
          <AlertTriangle className="h-6 w-6 text-destructive" />
        </div>
        <div className="space-y-2">
          <h1 className="text-xl font-bold tracking-tight">
            Si è verificato un errore
          </h1>
          <p className="text-sm text-muted-foreground">
            Abbiamo registrato il problema e il team tecnico è stato avvisato.
            Riprova oppure ricarica la pagina.
          </p>
        </div>
        <div className="flex items-center justify-center gap-2">
          <Button
            onClick={() => resetError()}
            variant="outline"
            data-testid="button-error-retry"
          >
            <RefreshCw className="h-4 w-4" />
            Riprova
          </Button>
          <Button
            onClick={() => window.location.assign("/")}
            data-testid="button-error-home"
          >
            Torna alla home
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Wraps the application tree with Sentry's React error boundary so any
 * uncaught render-time error is reported and the user sees a friendly
 * Italian fallback instead of a blank screen. When Sentry isn't
 * initialized (DSN missing) we still mount the boundary because
 * `Sentry.ErrorBoundary` works with or without an active hub — it just
 * skips the report and keeps the recovery UI.
 */
export function SentryErrorBoundary({ children }: Props) {
  return (
    <Sentry.ErrorBoundary
      fallback={({ resetError }) => <FallbackUi resetError={resetError} />}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}
